import {
  readBrowserStorageItem,
  removeBrowserStorageItem,
  writeBrowserStorageItem,
} from './browserStorage';

const CUSTOMER_SERVICE_BADGE_KEY_PREFIX = 'loan_customer_service_badge';

type CustomerServiceBadgeState = {
  unread: boolean;
  badgeDate: string;
};

const storageOptions = { order: ['session', 'local'] as const };

const normalizePhone = (phone: string) => String(phone || '').trim();
const getTodayBadgeDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getStorageKey = (phone: string) => `${CUSTOMER_SERVICE_BADGE_KEY_PREFIX}:${phone}`;

const readBadgeState = (phone: string): CustomerServiceBadgeState | null => {
  const raw = readBrowserStorageItem(getStorageKey(phone), storageOptions);
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as CustomerServiceBadgeState;
    if (typeof value?.unread !== 'boolean' || typeof value?.badgeDate !== 'string') {
      removeBrowserStorageItem(getStorageKey(phone), storageOptions);
      return null;
    }
    return value;
  } catch {
    removeBrowserStorageItem(getStorageKey(phone), storageOptions);
    return null;
  }
};

const writeBadgeState = (phone: string, state: CustomerServiceBadgeState) => {
  writeBrowserStorageItem(getStorageKey(phone), JSON.stringify(state), storageOptions);
};

export const clearCustomerServiceBadgeState = (phone: string) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) return;
  removeBrowserStorageItem(getStorageKey(normalizedPhone), storageOptions);
};

export const hasUnreadCustomerServiceBadge = (phone: string) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) return false;

  const current = readBadgeState(normalizedPhone);
  if (!current || current.badgeDate !== getTodayBadgeDate()) {
    return false;
  }
  return current?.unread ?? false;
};

export const ensureCustomerServiceBadge = (phone: string) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) return false;

  const today = getTodayBadgeDate();
  const current = readBadgeState(normalizedPhone);
  if (!current || current.badgeDate !== today) {
    writeBadgeState(normalizedPhone, { unread: true, badgeDate: today });
    return true;
  }

  return current.unread;
};

export const consumeCustomerServiceBadgeOnce = (phone: string) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) return false;

  const today = getTodayBadgeDate();
  const current = readBadgeState(normalizedPhone);
  if (!current || current.badgeDate !== today || !current.unread) {
    return false;
  }

  writeBadgeState(normalizedPhone, {
    unread: false,
    badgeDate: today,
  });
  return true;
};

export const markCustomerServiceBadgeUnread = (phone: string) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) return;

  const today = getTodayBadgeDate();
  const current = readBadgeState(normalizedPhone);
  if (current && current.badgeDate === today) {
    return;
  }

  writeBadgeState(normalizedPhone, { unread: true, badgeDate: today });
};

export const markCustomerServiceBadgeRead = (phone: string) => {
  const normalizedPhone = normalizePhone(phone);
  if (!normalizedPhone) return;

  const today = getTodayBadgeDate();
  const current = readBadgeState(normalizedPhone);
  if (!current || current.badgeDate !== today) {
    writeBadgeState(normalizedPhone, {
      unread: false,
      badgeDate: today,
    });
    return;
  }

  if (!current.unread) return;
  writeBadgeState(normalizedPhone, {
    unread: false,
    badgeDate: today,
  });
};
